<script setup>
import {computed, ref} from "vue";

// Define the slides array
const slides = ref([
	{ id: 1,  url: "helpers/Slide025.jpg",  title: "Headquarters",                 tags: ["Big Text"] },
	{ id: 2,  url: "helpers/Slide017.jpg",  title: "Headquarters",                 tags: ["Three Column", "Images"] },
	{ id: 3,  url: "helpers/Slide004.jpg", title: "Business philosophy",          tags: ["Three Column", "Text with Icon"] },
	{ id: 4,  url: "helpers/Slide195.jpg",  title: "North America map",            tags: ["Map", "Two Column"] },
	{ id: 5,  url: "helpers/Slide155.jpg",  title: "Our company in numbers",       tags: ["Data", "Fullpage", "Big Text"] },
	{ id: 6,  url: "helpers/Slide026.jpg",  title: "Long story",                   tags: ["Two Column", "Images"] },
	{ id: 7,  url: "helpers/Slide015.jpg", title: "Navigate together",            tags: ["Text with Icon", "Fullpage"] }, // "Four column" - removed because solo
	{ id: 8,  url: "helpers/Slide157.jpg",  title: "Comparing three departments",  tags: ["Data", "Two Column"] },
	{ id: 9,  url: "helpers/Slide035.jpg",  title: "Keep moving forward",          tags: ["Horizontal", "Images", "Detailed Text"] },
	{ id: 10, url: "helpers/Slide049.jpg",  title: "Our common services",          tags: ["Five Column", "Text with Icon"] },
	{ id: 11, url: "helpers/Slide003.jpg", title: "A few words",                  tags: ["Detailed Text"] },
	{ id: 12, url: "helpers/Slide158.jpg",  title: "Latest quarters",              tags: ["Data", "Two Column"] },
	{ id: 13, url: "helpers/Slide063.jpg",  title: "Clients around the globe",     tags: ["Grid", "Images"] },
	{ id: 14, url: "helpers/Slide068.jpg",  title: "Mobile App Project",           tags: ["Mockup", "Grid", "Text with Icon"] },
	{ id: 15, url: "helpers/Slide161.jpg",  title: "Four quarters stat",           tags: ["Data", "Two Column"] },
	{ id: 16, url: "helpers/Slide172.jpg",  title: "Word wide map",                tags: ["Map", "Diagram"] },
	{ id: 17, url: "helpers/Slide128.jpg",  title: "About us and our future",      tags: ["Horizontal", "Images"] },
	{ id: 18, url: "helpers/Slide069.jpg",  title: "User Interface",               tags: ["Mockup", "Grid", "Text with Icon"] },
	{ id: 19, url: "helpers/Slide165.jpg",  title: "Four years from now",          tags: ["Data", "Diagram"] },
	// { id: 20, url: "helpers/Slide129.jpg",  title: "01 02 03 Project Title",       tags: ["Images"] },
	{ id: 21, url: "helpers/Slide089.jpg",  title: "Website Layouts",              tags: ["Mockup", "Text with Icon"] },
	{ id: 22, url: "helpers/Slide138.jpg",  title: "Latest stats and reports",     tags: ["Two Column", "Images"] },
	{ id: 23, url: "helpers/Slide066.jpg",  title: "UI App Watch",                 tags: ["Mockup", "Three Column"] }
]);

const hoveredTag = ref(null);

// Compute unique tags for buttons
const uniqueTags = computed(() => {
	const allTags = displayedSlides.value.flatMap(slide => slide.tags);
	return [...new Set(allTags)].sort((a, b) => a.localeCompare(b)); // alphabetical sort
});

const isMobile = ref(window.innerWidth < 640);
const mobileSlideIds = [2, 3, 4, 16, 12, 15, 19, 14, 18, 23]; // Adjust based on preference


// Filter slides based on mobile condition
const displayedSlides = computed(() => {
	if (isMobile.value) {
		return slides.value.filter(slide => mobileSlideIds.includes(slide.id));
	}
	return slides.value;
});

const searchQuery = ref("");

// Only highlight slides that match the searchQuery, but do not remove them
const isHighlighted = (slide) => {
	return (
		(searchQuery.value.trim() && slide.title.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
		(hoveredTag.value && slide.tags.includes(hoveredTag.value))
	);
};
function getStyle(index) {
	return window.innerWidth < 640
		? `--tx: ${index * 23}px; --ty: ${index * 5}px; opacity: 1.3;` // mobile
		: `--tx: ${index * 30}px; --ty: ${index * 7}px; opacity: 1.3;`; // desktop
}

</script>

<template>

	<div class="relative">
		<div
			class="[mask-image:radial-gradient(120%_100%_at_15%_15%,black_80%,transparent_100%)] select-none size-full px-8 pt-20 sm:min-h-[240px] min-h-[150px]"
			aria-hidden="true"
		>
			<div class="[transform:rotateX(-18deg)_rotateY(23deg)] relative sm:w-[20%] w-[40%]">
				<div class="min-h-[90px]">
					<img
						v-for="(slide, index) in displayedSlides"
						:key="slide.id"
						:src="slide.url"
						:alt="slide.title"
						:class="isHighlighted(slide) ? 'border-2 border-blue-500 translate-y-[calc(var(--ty)-65px)]' : 'border border-gray-300 translate-y-[var(--ty)]'"
						class=" rounded-lg translate-x-[var(--tx)] hover:border-gray-400 transition-transform duration-300 absolute left-0 top-0 w-full"
						:style="getStyle(index)"
					/>
				</div>
			</div>
		</div>

		<div class="mx-8 -mt-2 relative rounded-md hidden sm:block">
			<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
				<svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"
					 xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd"
						  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
						  clip-rule="evenodd"></path>
				</svg>
			</div>
			<input name="searchQuery" id="searchQuery" v-model="searchQuery"
				   class="bg-gradient-to-r block focus:outline-none from-gray-100 border-0 h-10 pl-10 rounded-md sm:text-sm sm:w-1/2 to-transparent w-full"
				   placeholder="Search... try &quot;app&quot; or &quot;clients&quot; "  data-heap="search-for-icon">
		</div>
		<!--		<p class="mt-2 max-w-lg text-sm/6 text-gray-600 max-lg:text-center text-xs italic px-8">Try hovering on slide metadata.</p>-->
		<div class="flex flex-wrap sm:gap-2 gap-1 px-8 pb-8 mt-3">
			<button v-for="tag in uniqueTags" :key="tag"
					class="flex bg-gray-100 border border-gray-200 cursor-pointer font-medium hover:bg-white hover:text-gray-700 hover:border-gray-400 items-center px-2 py-1 relative rounded-xl text-gray-600 text-xs whitespace-nowrap"
					@mouseenter="hoveredTag = tag"
					@mouseleave="hoveredTag = null">
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="size-4 mr-1 text-gray-300">
					<path fill-rule="evenodd" d="M4.5 2A2.5 2.5 0 0 0 2 4.5v2.879a2.5 2.5 0 0 0 .732 1.767l4.5 4.5a2.5 2.5 0 0 0 3.536 0l2.878-2.878a2.5 2.5 0 0 0 0-3.536l-4.5-4.5A2.5 2.5 0 0 0 7.38 2H4.5ZM5 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd" />
				</svg>

				{{ tag }}
			</button>
		</div>
	</div>


</template>

<style scoped>

</style>